'use client'

import * as React from 'react'
import { useRouter } from 'next/navigation'
import { Search, Clock, TrendingUp, Package, Cpu, Zap } from 'lucide-react'
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command'
import { Kbd } from '@/components/ui/kbd'
import { Badge } from '@/components/ui/badge'

interface SearchResult {
  id: string
  part_number: string
  title: string
  description: string
  manufacturer_id: number
  category_id: number
  package_type: string
  specs: any
  images: any
  rank: number
  headline: string
  manufacturer_name?: string
  category_name?: string
}

interface CommandPaletteProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  const router = useRouter()
  const [query, setQuery] = React.useState('')
  const [results, setResults] = React.useState<SearchResult[]>([])
  const [loading, setLoading] = React.useState(false)
  const [recentSearches, setRecentSearches] = React.useState<string[]>([])

  // Load recent searches from localStorage
  React.useEffect(() => {
    const stored = localStorage.getItem('recentSearches')
    if (stored) {
      try {
        setRecentSearches(JSON.parse(stored))
      } catch (e) {
        console.error('Failed to parse recent searches', e)
      }
    }
  }, [])

  // Debounced search
  React.useEffect(() => {
    if (!query || query.trim().length < 2) {
      setResults([])
      return
    }

    const debounce = setTimeout(async () => {
      setLoading(true)
      try {
        const response = await fetch(
          `/api/search?q=${encodeURIComponent(query)}&limit=10`
        )
        if (response.ok) {
          const data = await response.json()
          console.log('Search API response:', data)
          console.log('Results count:', data.results?.length)
          setResults(data.results || [])
        }
      } catch (error) {
        console.error('Search failed:', error)
      } finally {
        setLoading(false)
      }
    }, 300)

    return () => clearTimeout(debounce)
  }, [query])

  const handleSelect = (productId: string, searchQuery: string) => {
    // Save to recent searches
    const updated = [searchQuery, ...recentSearches.filter(s => s !== searchQuery)].slice(0, 5)
    setRecentSearches(updated)
    localStorage.setItem('recentSearches', JSON.stringify(updated))

    // Navigate to product
    router.push(`/products/${productId}`)
    onOpenChange(false)
    setQuery('')
  }

  const handleRecentSearch = (search: string) => {
    setQuery(search)
  }

  const clearRecent = () => {
    setRecentSearches([])
    localStorage.removeItem('recentSearches')
  }

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput
        placeholder="Search 6M+ products by part number, description, or specifications..."
        value={query}
        onValueChange={setQuery}
      />
      <CommandList>
        <CommandEmpty>
          {loading ? (
            <div className="flex flex-col items-center justify-center py-6">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4"></div>
              <p className="text-sm text-muted-foreground">Searching...</p>
            </div>
          ) : query.trim().length < 2 ? (
            <div className="flex flex-col items-center justify-center py-6">
              <Search className="h-8 w-8 text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">Type at least 2 characters to search</p>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-6">
              <Package className="h-8 w-8 text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">No products found</p>
              <p className="text-xs text-muted-foreground mt-1">Try different keywords or part numbers</p>
            </div>
          )}
        </CommandEmpty>

        {/* Recent Searches */}
        {!query && recentSearches.length > 0 && (
          <>
            <CommandGroup heading="Recent Searches">
              {recentSearches.map((search, index) => (
                              <CommandItem
                key={index}
                onSelect={() => handleRecentSearch(search)}
                className="flex items-center gap-2 group"
                value={search}
              >
                <Clock className="h-4 w-4 text-muted-foreground transition-transform duration-200 group-hover:scale-110" />
                <span className="transition-colors duration-150">{search}</span>
              </CommandItem>
              ))}
            </CommandGroup>
            <CommandSeparator />
          </>
        )}

        {/* Quick Actions (when no query) */}
        {!query && (
          <CommandGroup heading="Quick Actions">
            <CommandItem onSelect={() => router.push('/products')} value="browse-products" className="group">
              <Package className="mr-2 h-4 w-4 transition-all duration-200 group-hover:scale-110 group-hover:text-blue-600" />
              <span>Browse All Products</span>
            </CommandItem>
            <CommandItem onSelect={() => router.push('/manufacturers')} value="view-manufacturers" className="group">
              <Cpu className="mr-2 h-4 w-4 transition-all duration-200 group-hover:scale-110 group-hover:text-blue-600" />
              <span>View Manufacturers</span>
            </CommandItem>
            <CommandItem onSelect={() => router.push('/industries')} value="explore-industries" className="group">
              <Zap className="mr-2 h-4 w-4 transition-all duration-200 group-hover:scale-110 group-hover:text-blue-600" />
              <span>Explore Industries</span>
            </CommandItem>
          </CommandGroup>
        )}

        {/* Search Results */}
        {results.length > 0 && (
          <CommandGroup heading={`Results (${results.length})`}>
            {results.map((result) => (
              <CommandItem
                key={result.id}
                value={`${result.part_number} ${result.title} ${result.description || ''} ${result.category_name || ''} ${result.manufacturer_name || ''}`}
                onSelect={() => handleSelect(result.id, query)}
                className="flex flex-col items-start gap-1 py-3 group cursor-pointer"
              >
                <div className="flex items-center gap-2 w-full">
                  <Package className="h-4 w-4 flex-shrink-0 text-blue-600 transition-transform duration-200 group-hover:scale-110" />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate transition-colors duration-150 group-hover:text-blue-600">
                      {result.part_number}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      {result.title}
                    </div>
                  </div>
                  {result.rank > 0.5 && (
                    <Badge variant="secondary" className="flex-shrink-0 transition-all duration-200 group-hover:bg-blue-100 group-hover:text-blue-700">
                      <TrendingUp className="h-3 w-3 mr-1" />
                      Top Match
                    </Badge>
                  )}
                </div>
                <div className="flex items-center gap-2 ml-6 text-xs text-muted-foreground">
                  {result.manufacturer_name && (
                    <span>{result.manufacturer_name}</span>
                  )}
                  {result.manufacturer_name && result.category_name && (
                    <span>•</span>
                  )}
                  {result.category_name && (
                    <span>{result.category_name}</span>
                  )}
                  {(result.manufacturer_name || result.category_name) && result.package_type && (
                    <span>•</span>
                  )}
                  {result.package_type && (
                    <span>{result.package_type}</span>
                  )}
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {/* Footer Hint */}
        {!loading && (
          <div className="border-t px-4 py-3 text-xs text-muted-foreground flex items-center justify-between bg-slate-50/50">
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1 transition-all duration-200 hover:text-foreground">
                <Kbd keys={['↑', '↓']}>↑↓</Kbd> Navigate
              </span>
              <span className="flex items-center gap-1 transition-all duration-200 hover:text-foreground">
                <Kbd keys={['enter']}>↵</Kbd> Select
              </span>
              <span className="flex items-center gap-1 transition-all duration-200 hover:text-foreground">
                <Kbd keys={['esc']}>⎋</Kbd> Close
              </span>
            </div>
            {recentSearches.length > 0 && (
              <button
                onClick={clearRecent}
                className="text-xs hover:text-foreground transition-all duration-200 hover:underline"
              >
                Clear recent
              </button>
            )}
          </div>
        )}
      </CommandList>
    </CommandDialog>
  )
}
